import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQuery } from '@apollo/client/react';
import TemplateAndTemplatesQuery from 'queries/template/index_and_show';
import TemplatesQuery from 'queries/template/index';
import OptionableAutocomplete from 'components/form/autocomplete/optionable';

const Template = ({
  id,
  label,
  name,
  onChange,
  onBlur,
  value,
  values,
  setValue,
  error,
  helperText,
}) => {
  const intl = useIntl();
  const [searchValue, setSearchValue] = useState(null);
  const [defaultValue, setDefaultValue] = useState(null);
  const processedTemplateIdRef = useRef(null);

  const { data = { templates: { templates: [], templatesTotal: 0 }, template: null }, loading } =
    useQuery(value ? TemplateAndTemplatesQuery : TemplatesQuery, {
      variables: { page: 1, name: searchValue, templateId: value },
    });

  const setTemplateData = useCallback(
    (template) => {
      if (template.periodNumber) {
        setValue('periodNumber', template.periodNumber);
      }
      if (template.periodCycle) {
        setValue('periodCycle', template.periodCycle);
      }
      let fields = [
        ...values.inspectionFields,
        ...template.templateFields.map((field) => ({
          name: field.name,
          templateFieldId: field.id,
        })),
      ];
      setValue('inspectionFields', fields);
    },
    [setValue, values.inspectionFields]
  );

  useEffect(() => {
    if (!loading && data?.template) {
      if (processedTemplateIdRef.current === data.template.id) return;
      processedTemplateIdRef.current = data.template.id;
      if (!defaultValue) {
        setDefaultValue({ label: data.template.name, value: data.template.id });
      }
      setTemplateData(data.template);
    }
  }, [data, loading, defaultValue, setTemplateData]);

  const options = useMemo(
    () =>
      data.templates.templates.map((template) => ({ label: template.name, value: template.id })),
    [data]
  );

  const resetFields = useCallback(() => {
    let fields = [];
    values.inspectionFields.forEach((field) => {
      if (!field.templateFieldId) {
        fields.push(field);
      }
    });
    setValue('inspectionFields', fields);
  }, [setValue, values.inspectionFields]);

  const onChangeInternal = useCallback(
    (evt, value) => {
      processedTemplateIdRef.current = null;
      resetFields();
      setDefaultValue(value);
      onChange(evt, value);
    },
    [onChange, resetFields]
  );

  return (
    <OptionableAutocomplete
      id={id}
      label={label}
      options={options}
      name={name}
      onChange={onChangeInternal}
      loadingText={intl.formatMessage({ id: 'autocomplete.template.loading' })}
      noOptionsText={<FormattedMessage id="autocomplete.template.no_options" />}
      value={defaultValue}
      onBlur={onBlur}
      loading={loading}
      error={error}
      helperText={helperText}
      onSearch={setSearchValue}
    />
  );
};

export default Template;
